import 'cropperjs/dist/cropper.css'

import { Grid, Stack, Stepper, Text, Title, useMantineTheme } from '@mantine/core'
import { useDisclosure, useMediaQuery } from '@mantine/hooks'
import { IconCheck, IconPlayerPlayFilled } from '@tabler/icons-react'
import sha256 from 'crypto-js/sha256'
import { useLiveQuery } from 'dexie-react-hooks'
import { closeSnackbar, enqueueSnackbar } from 'notistack'
import React, { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ReactCropperElement } from 'react-cropper'
import { Trans, useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import useSpaceRegistry from '@/hooks/useSpaceRegistry'
import { useAuth } from '@/plugins/auth'
import { useDb } from '@/plugins/db/Db'
import { baseAddressUrl } from '@/utils/table'
import {
  containsJsonAttributes,
  convertUnitToBytes,
  dataURLtoFile,
  Draft,
  ImmutableDraft,
} from '@/utils/utils'

import { globalConstants } from '../../constants/content'
import useObjectState from '../../hooks/useObjectState'
import { BodyStack } from '../../styled/components'
import BackButton from '../Buttons/BackButton'
import RootButton from '../Buttons/RootButton'
import ErrorMessage from '../ErrorMessage'
import HelpDrawer from '../HelpDrawer/HelpDrawer'
import CropItemsModal from '../Modals/Items/CropItemsModal'
import ModalSkeleton from '../Modals/ModalSkeleton'
import Snackbar from '../Snackbar'
import {
  Asset,
  Checkout,
  CompletedStepper,
  Info,
  Properties,
  Rights,
  Tags,
} from './StepperElements'
import Credits from './StepperElements/Credits'
import ItemPreview from './StepperElements/Image/ItemPreview'
import { ArtItem, SpaceLasersItem } from './Templates/utils'

export const MAX_DRAFTS_SIZE = 100
export const MAX_DRAFTS_UNIT = 'MB'
export const jsonSize = convertUnitToBytes(500, 'KB')

// TODO: update stepper steps with credits (key = creator tho) and comment royalties for now
const STEPPER_STEPS = [
  'asset',
  'info',
  'tags',
  'properties',
  'rights',
  'creator',
  // 'royalties',
  'complete',
] as const

const defaultNewItem = Object.freeze({
  // Needed for the stepper
  id: '',
  activeStep: 0,
  resaleRoyaltiesChecked: false,
  activeRightsStep: 1,
  responseValues: {},
  preQuestionIds: {},

  // Used for Minting
  creator: {
    name: '',
    link: '',
    credits: [],
  },
  asset: '',
  cropInfo: {
    x: 0,
    y: 0,
    width: globalConstants.CROP_IMAGE_SIZE.width,
    height: globalConstants.CROP_IMAGE_SIZE.height,
    rotate: 0,
    scaleX: 1,
    scaleY: 1,
  },
  name: '',
  description: '',
  tags: [],
  displayProperties: [{ key: '', value: '' }],
  properties: {},
  rights: {
    retainRights: false,
    requireAttributions: false,
    allowCommercialUse: false,
    allowAI: false,
  },
  royalties: {
    salePercentage: 0,
    transferFee: 0,
    royaltyRecipients: [
      {
        recipient: '',
        percentage: 1,
      },
    ],
  },
  storage: 'ipfs',
} as const satisfies ImmutableDraft)

const newDraft: (
  initialState: Partial<ImmutableDraft | Draft>
) => ImmutableDraft = initialState => {
  // Create a deep copy of the defaultNewItem object
  const ret: Draft = JSON.parse(JSON.stringify(defaultNewItem))

  // If initialState is not provided, return the copy of defaultNewItem
  if (!initialState) return Object.freeze(ret)

  // Get the keys of initialState
  const keys = Object.keys(initialState) as (keyof typeof initialState)[]

  // Filter out keys that have undefined values or empty arrays
  const validKeys = keys.filter(k => {
    const initProp = initialState[k]
    return initProp !== undefined && (!Array.isArray(initProp) || initProp.length)
  })

  // For each valid key, update the corresponding property in the copied defaultNewItem object
  validKeys.forEach(k => {
    ;(ret[k] as any) = initialState[k]
  })

  return Object.freeze(ret)
}

const Component: React.FC = () => {
  const { t } = useTranslation()
  const theme = useMantineTheme()
  const navigate = useNavigate()
  const { address } = useAuth()
  const { state } = useLocation()
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`)
  const itemPreviewDrawer = useMediaQuery(`(max-width: ${theme.breakpoints.md})`)
  const [error, setError] = useState<string>('')
  const { db } = useDb()

  // Get template ID from state link
  const [spacesId, templateId] = useMemo(
    () => state?.id?.split('-') || state?.draftData?.id?.split('-') || [],
    [state]
  )

  useEffect(() => {
    if (!spacesId || !templateId) {
      navigate('/studio')
    }
  }, [spacesId, templateId, navigate])

  // Modal Variables
  const [opened, { open, close }] = useDisclosure(false)
  const [openedDraftMsg, { open: openDraftMsg, close: closeDraftMsg }] = useDisclosure(false)
  const tryingToExit = useRef<boolean>(false)

  const cropperRef = useRef<ReactCropperElement>()

  const { spacesMetadata } = useSpaceRegistry()

  const itemData: SpaceLasersItem | ArtItem | undefined = useMemo(
    () =>
      spacesId &&
      templateId &&
      spacesMetadata?.[spacesId]?.metadataInfo?.templates[templateId]?.item,
    [spacesId, templateId, spacesMetadata]
  )
  const displayProperties = useMemo(
    () =>
      spacesId &&
      templateId &&
      spacesMetadata?.[spacesId]?.metadataInfo?.templates[templateId]?.displayProperties,
    [spacesId, templateId, spacesMetadata]
  )

  const contractAddress = useMemo(
    () =>
      spacesId &&
      templateId &&
      spacesMetadata?.[spacesId]?.metadataInfo?.templates[templateId]?.address,
    [spacesId, templateId, spacesMetadata]
  )

  // Check if template.json is valid
  useEffect(() => {
    if (!itemData) {
      setError('Error with template.json: item key is undefined')
    }
  }, [itemData])

  // Metadata
  const templateAttributes = useMemo(
    () =>
      itemData &&
      containsJsonAttributes(itemData as unknown as Record<any, unknown>, STEPPER_STEPS),
    [itemData]
  )
  // number of steps being used from STEPPER_STEPS + checkout (that is always used)
  const numSteps = useMemo(
    () => templateAttributes && Object.values(templateAttributes).filter(Boolean).length + 1,
    [templateAttributes]
  )

  // State currentItem from Draft
  const [currentItem, , setItemProperty, currentItemRef] = useObjectState(
    newDraft,
    state?.draftData
  )
  const [image, setImage] = useState<File | null>(
    // TODO: Draft key asset is a string if it's a base64 image, otherwise it's a File. It should be possibly null when empty
    currentItem?.asset && currentItem?.asset !== null ? (currentItem?.asset as File) : null
  )
  const [exceedsImageSize, setExceedsImageSize] = useState<boolean>(false)
  const [invalidType, setInvalidType] = useState<boolean>(false)

  const [previewImage, setPreviewImage] = useState<string>('')
  const hash = useMemo(
    () => (previewImage ? sha256(JSON.stringify(previewImage)).toString().substring(0, 8) : ''),
    [previewImage]
  )
  const isSameItem = useCallback((draftId: string) => draftId === hash, [hash])

  // update draftData with creator
  useEffect(() => {
    if (!address) return
    setItemProperty('creator')({
      name: address,
      link: `${baseAddressUrl}${address}`,
    })
  }, [address, state])

  // update draftData with displayProperties
  useEffect(() => {
    if (!displayProperties) return
    setItemProperty('displayProperties')(displayProperties)
  }, [displayProperties, setItemProperty, state])

  // update draftData if templateId or spacesId changes
  useEffect(() => {
    if (!spacesId || !templateId) return
    const setItemId = setItemProperty('id')
    setItemId(`${spacesId}-${templateId}`)
  }, [spacesId, templateId, state, setItemProperty])

  // update draftData if tags from template metadata change
  useEffect(() => {
    const tags = state?.tags
    if (!tags) return
    setItemProperty('tags')(tags)
  }, [setItemProperty, state])

  // Stepper
  const nextStep = useCallback(
    () =>
      numSteps &&
      setItemProperty('activeStep')(current => (current < numSteps ? current + 1 : current)),
    [numSteps, setItemProperty]
  )
  const prevStep = useCallback(
    () => setItemProperty('activeStep')(current => (current > 0 ? current - 1 : current)),
    [setItemProperty]
  )

  // Drafts
  const allDrafts = useLiveQuery(() => db?.table('drafts').toArray() ?? [], [db])

  const deleteDraft = useCallback(() => {
    if (db && hash) {
      db.table('drafts').where('id').equals(hash).delete()
    }
  }, [db, hash])

  const [draftsSizeInBytes, setDraftsSizeInBytes] = useState<number>(0)

  // get drafts size from indexed db dexie
  useEffect(() => {
    const getDraftsSize = async () => {
      if (db) {
        await db
          .table('drafts')
          .toArray()
          .then(drafts => {
            const size = drafts.reduce((acc, draft) => {
              const imageSize = draft.draftJson.asset.size
              return acc + imageSize + jsonSize
            }, 0)
            setDraftsSizeInBytes(size)
          })
      }
    }
    getDraftsSize()
  }, [db])

  const saveDraft = useCallback(
    (snackbar = false) => {
      if (!numSteps) return
      const draftJson = currentItemRef.current

      if (!draftJson.asset || !image) {
        return
      }
      if (
        draftsSizeInBytes + (image?.size || 0) + jsonSize >
          convertUnitToBytes(MAX_DRAFTS_SIZE, MAX_DRAFTS_UNIT) &&
        tryingToExit.current &&
        allDrafts &&
        !allDrafts.some(draft => isSameItem(draft.id))
      ) {
        openDraftMsg()
        tryingToExit.current = false
        return
      }
      if (db && hash) {
        const timestamp = Date.now()
        try {
          // convert base64 asset to File
          const draftImage = dataURLtoFile(draftJson.asset as string)
          // hash the image to get the id
          db.table('drafts').put({
            id: hash,
            draftJson: { ...draftJson, asset: draftImage },
            timestamp,
          })
        } finally {
          // Show snackbar notification
          if (snackbar) {
            closeSnackbar()
            enqueueSnackbar(
              <Snackbar
                value={t('components.studio.snackbar.draftSavedBody', 'Your draft has been saved')}
                title={t('components.studio.snackbar.draftSavedTitle', 'Draft Saved')}
                icon={<IconCheck size="14px" />}
              />
            )
          }
        }
      }
    },
    [
      currentItemRef,
      image,
      numSteps,
      draftsSizeInBytes,
      db,
      hash,
      allDrafts,
      tryingToExit,
      openDraftMsg,
      t,
    ]
  )

  const handleAfterClick = useCallback(() => {
    tryingToExit.current = true
    if (!numSteps) return
    if (currentItem.asset !== '' && currentItem.activeStep < numSteps) {
      saveDraft(true)
    } else if (allDrafts && allDrafts.some(draft => isSameItem(draft.id))) {
      deleteDraft()
    }
  }, [numSteps, currentItem, allDrafts, saveDraft, deleteDraft, currentItemRef])

  useEffect(() => {
    if (image) {
      // set image to preview
      const reader = new FileReader()
      reader.readAsDataURL(image)
      reader.onload = () => {
        let { result } = reader
        if (result === null || result === undefined) return

        setPreviewImage(result as string)

        if (result instanceof ArrayBuffer) {
          result = `data:;base64,${Buffer.from(result).toString('base64')}`
        }

        setItemProperty('asset')(result as string)
      }
    } else {
      setPreviewImage('')
      setItemProperty('asset')('')
    }
  }, [image, setItemProperty])

  // Crop Mode
  const cropThumbnail = () => {
    if (typeof cropperRef.current?.cropper !== 'undefined') {
      setItemProperty('cropInfo')(cropperRef.current?.cropper.getData())
    }
  }

  const getIconsFromResponseValues = useCallback(() => {
    const rightsJson = itemData?.rights
    if (!rightsJson) return []

    const icons: string[] = []

    Object.keys(currentItem.responseValues).forEach(questionId => {
      const answerValue = currentItem.responseValues[Number(questionId)]
      const question = rightsJson?.rules?.questions?.find(q => q.id === Number(questionId))

      if (question) {
        const answer = question.answers.find(a => a.value === Number(answerValue))

        if (answer && answer.icon) {
          icons.push(answer.icon)
        }
      }
    })
    return icons
  }, [currentItem.responseValues, itemData?.rights])

  const itemPreview = (
    <ItemPreview
      title={currentItem.name}
      description={currentItem.description}
      tags={currentItem.tags}
      metadata={itemData}
      previewImage={previewImage}
      setImage={setImage}
      setExceedsImageSize={setExceedsImageSize}
      setInvalidType={setInvalidType}
      rightsIcons={getIconsFromResponseValues()}
      cropInfo={currentItem.cropInfo}
      openCropper={open}
      setCropInfo={setItemProperty('cropInfo')}
    />
  )

  return (
    <>
      {!error && itemData ? (
        <>
          <ModalSkeleton
            opened={opened}
            onClose={close}
            title={t('components.studio.cropThumbnail', 'Crop Cover Image')}
          >
            <CropItemsModal
              onConfirm={() => {
                cropThumbnail()
                close()
              }}
              asset={previewImage}
              cropperRef={cropperRef as MutableRefObject<ReactCropperElement>}
            />
          </ModalSkeleton>
          <ModalSkeleton
            opened={openedDraftMsg}
            onClose={closeDraftMsg}
            title={t('components.studio.draftMsg.title', 'Drafts Limit Reached')}
          >
            <Text style={{ fontSize: '16px' }}>
              <Trans i18nKey="components.studio.draftMsg.body">
                You have reached the maximum size of {{ MAX_DRAFTS_SIZE }}
                {{ MAX_DRAFTS_UNIT }}. Please delete a draft to create a new one or confirm you want
                to go back without saving this one.
              </Trans>
            </Text>
            <Stack style={{ flexDirection: 'row', marginTop: '20px' }}>
              <RootButton
                onClick={() => {
                  closeDraftMsg()
                }}
                secondary
                expand
                style1={false}
              >
                {t('buttons.cancel', 'Cancel')}
              </RootButton>
              <RootButton
                onClick={() => {
                  navigate('/studio')
                }}
                expand
                style1={false}
              >
                {t('buttons.confirm', 'Confirm')}
              </RootButton>
            </Stack>
          </ModalSkeleton>
          <Grid style={{ flexDirection: 'row', minHeight: '100vh' }}>
            <Grid.Col span="auto">
              <BodyStack style={{ marginLeft: '25px', marginRight: '25px' }}>
                <BackButton
                  exit
                  navTo={
                    draftsSizeInBytes + (image?.size || 0) + jsonSize >
                      convertUnitToBytes(MAX_DRAFTS_SIZE, MAX_DRAFTS_UNIT) &&
                    currentItem.asset &&
                    allDrafts &&
                    !allDrafts.some(draft => isSameItem(draft.id)) &&
                    numSteps &&
                    currentItem.activeStep !== numSteps
                      ? 'cancelNav'
                      : '/studio'
                  }
                  afterClick={handleAfterClick}
                />
                <Title order={isMobile ? 2 : 1}>
                  {t('components.studio.createitem', 'Create Item')}
                </Title>
                {templateAttributes ? (
                  <Stack style={{ flexDirection: 'row' }}>
                    <Stepper
                      color={theme.colors.l1Primary[0]}
                      size="sm"
                      iconSize={12}
                      active={currentItem.activeStep}
                      onStepClick={setItemProperty('activeStep')}
                      allowNextStepsSelect={false}
                    >
                      {templateAttributes.asset && (
                        <Stepper.Step icon={<></>} label={isMobile ? '' : 'UPLOAD'}>
                          <Asset
                            image={image}
                            setImage={setImage}
                            nextStep={nextStep}
                            exceedsImageSize={exceedsImageSize}
                            invalidType={invalidType}
                            setExceedsImageSize={setExceedsImageSize}
                            setInvalidType={setInvalidType}
                            metadata={itemData}
                            saveDraft={saveDraft}
                            openCropper={open}
                            setCropInfo={setItemProperty('cropInfo')}
                          />
                        </Stepper.Step>
                      )}
                      {templateAttributes.info && (
                        <Stepper.Step
                          icon={<></>}
                          label={isMobile ? '' : STEPPER_STEPS[1].toUpperCase()}
                        >
                          <Info
                            title={currentItem.name}
                            setTitle={setItemProperty('name')}
                            description={currentItem.description}
                            setDescription={setItemProperty('description')}
                            prevStep={prevStep}
                            nextStep={nextStep}
                            metadata={itemData}
                            saveDraft={saveDraft}
                          />
                        </Stepper.Step>
                      )}
                      {templateAttributes.tags && (
                        <Stepper.Step
                          icon={<></>}
                          label={isMobile ? '' : STEPPER_STEPS[2].toUpperCase()}
                        >
                          <Tags
                            tags={currentItem.tags}
                            setTags={setItemProperty('tags')}
                            prevStep={prevStep}
                            nextStep={nextStep}
                            metadata={itemData}
                            saveDraft={saveDraft}
                          />
                        </Stepper.Step>
                      )}
                      {templateAttributes.properties && (
                        <Stepper.Step
                          icon={<></>}
                          label={isMobile ? '' : STEPPER_STEPS[3].toUpperCase()}
                        >
                          <Properties
                            prevStep={prevStep}
                            nextStep={nextStep}
                            metadata={itemData}
                            saveDraft={saveDraft}
                            properties={currentItem.properties}
                            setProperties={setItemProperty('properties')}
                          />
                        </Stepper.Step>
                      )}

                      {templateAttributes.rights && (
                        <Stepper.Step
                          icon={<></>}
                          label={isMobile ? '' : STEPPER_STEPS[4].toUpperCase()}
                        >
                          <Rights
                            activeStep={currentItem.activeRightsStep}
                            setActiveStep={setItemProperty('activeRightsStep')}
                            responseValues={currentItem.responseValues}
                            setResponseValues={setItemProperty('responseValues')}
                            setRights={setItemProperty('rights')}
                            prevStep={prevStep}
                            nextStep={nextStep}
                            saveDraft={saveDraft}
                            metadata={itemData}
                          />
                        </Stepper.Step>
                      )}
                      {templateAttributes.creator && (
                        <Stepper.Step icon={<></>} label={isMobile ? '' : 'CREDITS'}>
                          <Credits
                            prevStep={prevStep}
                            nextStep={nextStep}
                            saveDraft={saveDraft}
                            metadata={itemData}
                            creator={currentItem.creator}
                            setCreator={setItemProperty('creator')}
                          />
                        </Stepper.Step>
                      )}
                      {/* {templateAttributes.royalties && (
                        <Stepper.Step icon={<></>} label={isMobile ? '' : STEPPER_STEPS[5].toUpperCase()}>
                          <Royalties
                            prevStep={prevStep}
                            nextStep={nextStep}
                            metadata={itemData}
                            resaleRoyaltiesChecked={currentItem.resaleRoyaltiesChecked}
                            setResaleRoyaltiesChecked={setItemProperty('resaleRoyaltiesChecked')}
                            royalties={currentItem.royalties}
                            setRoyalties={setItemProperty('royalties')}
                          />
                        </Stepper.Step>
                      )} */}
                      <Stepper.Step icon={<></>} label={isMobile ? '' : 'PUBLISH'}>
                        <Checkout
                          contractAddress={contractAddress as string}
                          title={currentItem.name}
                          previewImage={previewImage}
                          prevStep={prevStep}
                          nextStep={nextStep}
                          // selectedStorage={currentItem.storage}
                          // setSelectedStorage={setItemProperty('storage')}
                          imageSize={image?.size}
                          itemJson={currentItem}
                          metadata={itemData}
                          deleteDraft={deleteDraft}
                        />
                      </Stepper.Step>
                      <Stepper.Completed>
                        <CompletedStepper
                          itemName={currentItem.name}
                          metadata={itemData}
                          templateId={templateId}
                        />
                      </Stepper.Completed>
                    </Stepper>
                  </Stack>
                ) : (
                  <ErrorMessage
                    bug
                    message={t('components.items.errorTemplate', 'Error with Spaces Template')}
                    details="Error with template.json: item key is undefined"
                  />
                )}
              </BodyStack>
            </Grid.Col>
            {!itemPreviewDrawer && (
              <Grid.Col
                span="auto"
                style={{ backgroundColor: theme.colors.background2[0], minHeight: '100vh' }}
              >
                {itemPreview}
              </Grid.Col>
            )}
          </Grid>
          {itemPreviewDrawer && (
            <HelpDrawer
              title={t('components.items.previewItem', 'Item Preview')}
              content={itemPreview}
              icon={<IconPlayerPlayFilled />}
            />
          )}
        </>
      ) : (
        <BodyStack>
          <BackButton />
          <ErrorMessage
            bug
            message={t('components.items.errorTemplate', 'Error with Spaces Template')}
            details={error}
          />
        </BodyStack>
      )}
    </>
  )
}
Component.displayName = 'CreateItem'

export { Component }
